import * as React from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import { JobRequestFile, JobRequestsService, LinguisticQualityReviewResult } from "../../../gen/clients/llts";
import { StarRating } from "../../StarRating/StarRating";
import { InfoIcon } from "../../InfoIcon/InfoIcon";
import { useTranslation } from "react-i18next";

interface Props {
  jobRequestId: string;
  files: JobRequestFile[] | undefined;
  showAiLinguisticQualityScorePerFile?: boolean;
  selectable?: boolean;
  selectedFileKeys?: string[];
  onSelectionChange?: (fileKeys: string[]) => void;
  checkboxTooltip?: string;
  /** List of file extensions (without dot) that can be selected. If not provided, all files are selectable. */
  selectableFileExtensions?: string[];
  /** Whether to group files by language. Defaults to true. */
  groupByLanguage?: boolean;
  /** Function to get the language name for a given language ID. If not provided, the language ID will be used. */
  getLanguageName?: (languageId: string | number | undefined) => string | null;
  /** Linguistic quality review results for each language. Used to display star rating in language group headers. */
  linguisticQualityReviewResults?: LinguisticQualityReviewResult[];
}

/**
 * Represents a component that renders a list of files associated with a job request.
 *
 * @component
 * @param {Object} Props - The properties of the component.
 * @param {string} Props.jobRequestId - The ID of the job request.
 * @param {Array} Props.files - The list of files associated with the job request.
 * @param {boolean} Props.showAiLinguisticQualityScorePerFile - Whether to show AI linguistic quality scores for individual files.
 */
const JobRequestFileList: React.FC<Props> = ({
  jobRequestId,
  files,
  showAiLinguisticQualityScorePerFile = false,
  selectable = false,
  selectedFileKeys,
  onSelectionChange,
  checkboxTooltip,
  selectableFileExtensions,
  groupByLanguage,
  getLanguageName,
  linguisticQualityReviewResults
}) => {
  const { t } = useTranslation();
  const isFileSelectable = React.useCallback(
    (fileName: string): boolean => {
      if (!selectableFileExtensions || selectableFileExtensions.length === 0) {
        return true;
      }
      const extension = fileName.split(".").pop()?.toLowerCase();
      return extension ? selectableFileExtensions.map(ext => ext.toLowerCase()).includes(extension) : false;
    },
    [selectableFileExtensions]
  );
  const onFileLinkClick = React.useCallback(
    async (file: JobRequestFile) => {
      const downloadUrlResponse = await JobRequestsService.getJobRequestFileDownloadUrl({
        jobRequestId,
        fileKey: encodeURIComponent(file.fileKey)
      });
      const downloadUrl = downloadUrlResponse.downloadUrl;
      if (downloadUrl) {
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.click();
      }
    },
    [jobRequestId]
  );

  const handleFileCheckboxChange = React.useCallback(
    (file: JobRequestFile, checked: boolean) => {
      if (!onSelectionChange) {
        return;
      }
      const currentKeys = selectedFileKeys || [];
      if (checked) {
        if (!currentKeys.includes(file.fileKey)) {
          onSelectionChange([...currentKeys, file.fileKey]);
        }
      } else {
        onSelectionChange(currentKeys.filter(k => k !== file.fileKey));
      }
    },
    [onSelectionChange, selectedFileKeys]
  );

  // Get all selectable files
  const selectableFiles = React.useMemo(() => {
    if (!files) {
      return [];
    }
    return files.filter(f => isFileSelectable(f.fileName));
  }, [files, isFileSelectable]);

  // Check if all selectable files are selected
  const allSelectableFilesSelected = React.useMemo(() => {
    if (selectableFiles.length === 0) {
      return false;
    }
    return selectableFiles.every(f => selectedFileKeys?.includes(f.fileKey));
  }, [selectableFiles, selectedFileKeys]);

  // Check if some (but not all) selectable files are selected
  const someSelectableFilesSelected = React.useMemo(() => {
    if (selectableFiles.length === 0) {
      return false;
    }
    const selectedCount = selectableFiles.filter(f => selectedFileKeys?.includes(f.fileKey)).length;
    return selectedCount > 0 && selectedCount < selectableFiles.length;
  }, [selectableFiles, selectedFileKeys]);

  // Handle select/deselect all
  const handleSelectAllChange = React.useCallback(
    (checked: boolean) => {
      if (!onSelectionChange) {
        return;
      }
      if (checked) {
        // Select all selectable files
        const allSelectableKeys = selectableFiles.map(f => f.fileKey);
        // Merge with existing selections (in case there are files from other sources)
        const currentKeys = selectedFileKeys || [];
        const newKeys = [...new Set([...currentKeys, ...allSelectableKeys])];
        onSelectionChange(newKeys);
      } else {
        // Deselect all files from this list
        const selectableKeysSet = new Set(selectableFiles.map(f => f.fileKey));
        const currentKeys = selectedFileKeys || [];
        onSelectionChange(currentKeys.filter(k => !selectableKeysSet.has(k)));
      }
    },
    [onSelectionChange, selectableFiles, selectedFileKeys]
  );

  // Group files by language
  const groupedFiles = React.useMemo(() => {
    if (!files || files.length === 0) {
      return {};
    }

    // If we don't have multiple languages or grouping is disabled, put all files in one group
    if (!groupByLanguage) {
      return { "": files };
    }

    // Group files by languageId
    const groups: { [key: string]: JobRequestFile[] } = {};
    files.forEach(file => {
      const langId = file.languageId ? String(file.languageId) : "";
      if (!groups[langId]) {
        groups[langId] = [];
      }
      groups[langId].push(file);
    });

    return groups;
  }, [files, groupByLanguage]);

  const renderFileItem = React.useCallback(
    (f: JobRequestFile) => {
      const isSelected = selectedFileKeys?.includes(f.fileKey) ?? false;
      const canSelect = isFileSelectable(f.fileName);
      return (
        <Box key={f.fileName} display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" flexGrow={1}>
            {selectable && (
              <Tooltip
                title={(canSelect ? checkboxTooltip : t("common.fileTypeNotSupported")) || ""}
                disableHoverListener={!checkboxTooltip || (selectedFileKeys?.length || 0) > 0}
              >
                <span>
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    disabled={!canSelect}
                    onChange={event => handleFileCheckboxChange(f, event.target.checked)}
                    sx={{ p: 0, m: 0, mr: 1 }}
                    disableRipple={true}
                  />
                </span>
              </Tooltip>
            )}
            <Typography variant="body2">
              <Link href="#" onClick={() => onFileLinkClick(f)}>
                {f.fileName}
              </Link>
            </Typography>
          </Box>
          {showAiLinguisticQualityScorePerFile && f.linguisticQualityScore !== undefined && (
            <Box display="flex" alignItems="center" sx={{ ml: 2 }}>
              <StarRating rating={f.linguisticQualityScore} size="small" />
              {f.linguisticQualityScoreReason && (
                <Box ml={0.5} display="flex" alignItems="center">
                  <InfoIcon tooltip={f.linguisticQualityScoreReason} size="small" variant="outlined" />
                </Box>
              )}
            </Box>
          )}
        </Box>
      );
    },
    [
      selectedFileKeys,
      isFileSelectable,
      selectable,
      checkboxTooltip,
      t,
      showAiLinguisticQualityScorePerFile,
      handleFileCheckboxChange,
      onFileLinkClick
    ]
  );

  // Sort groups by language name alphabetically
  const sortedGroupEntries = React.useMemo(() => {
    const entries = Object.entries(groupedFiles);
    if (!getLanguageName) {
      return entries;
    }
    return entries.sort(([langIdA], [langIdB]) => {
      // Empty language ID (no language) should come first
      if (langIdA === "") {
        return -1;
      }
      if (langIdB === "") {
        return 1;
      }
      // Sort by language name alphabetically
      const nameA = getLanguageName(langIdA) || langIdA;
      const nameB = getLanguageName(langIdB) || langIdB;
      return (nameA || "").localeCompare(nameB || "");
    });
  }, [groupedFiles, getLanguageName]);

  return (
    <Stack spacing={1}>
      {/* Select All checkbox */}
      {selectable && selectableFiles.length > 0 && (
        <Box display="flex" alignItems="center">
          <Checkbox
            size="small"
            checked={allSelectableFilesSelected}
            indeterminate={someSelectableFilesSelected}
            onChange={event => handleSelectAllChange(event.target.checked)}
            sx={{ p: 0, m: 0, mr: 1 }}
            disableRipple={true}
          />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t("common.selectAll")}
          </Typography>
        </Box>
      )}
      {sortedGroupEntries.map(([languageId, groupFiles]) => {
        const hasLanguageHeader = languageId !== "";
        const languageName = hasLanguageHeader ? (getLanguageName ? getLanguageName(languageId) : languageId) : null;
        const reviewResult = linguisticQualityReviewResults?.find(r => r.languageId === languageId);

        return (
          <Box key={languageId || "no-language"}>
            {hasLanguageHeader && languageName && (
              <Stack direction="row" spacing={3} alignItems="center" sx={{ mt: 1, mb: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {languageName}
                </Typography>
                {reviewResult && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <StarRating rating={reviewResult.score} size="small" />
                    {reviewResult.scoreReason && (
                      <InfoIcon tooltip={reviewResult.scoreReason} size="small" variant="outlined" />
                    )}
                  </Stack>
                )}
              </Stack>
            )}
            <Stack spacing={0.5} sx={hasLanguageHeader ? { pl: 2 } : {}}>
              {groupFiles.map(f => renderFileItem(f))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export { JobRequestFileList };
